import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import { FiPlus, FiEdit, FiTrash2, FiX } from 'react-icons/fi';
import './Management.css';

const BrandsManagement = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    logo_path: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      const response = await adminAPI.getAllBrands();
      setBrands(response.data);
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await adminAPI.updateBrand(editingBrand.id, formData);
      } else {
        await adminAPI.createBrand(formData);
      }
      loadBrands();
      resetForm();
    } catch (error) {
      alert('Error al guardar la marca');
      console.error(error);
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name || '',
      logo_path: brand.logo_path || '',
      description: brand.description || '',
      is_active: brand.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta marca?')) {
      try {
        await adminAPI.deleteBrand(id);
        loadBrands();
      } catch (error) {
        alert('Error al eliminar la marca');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logo_path: '',
      description: '',
      is_active: true,
    });
    setEditingBrand(null);
    setShowForm(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await adminAPI.uploadFile(file, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      });
      setFormData({ ...formData, logo_path: response.data.file_path });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <div className="management">
      <div className="management-header">
        <h1>Gestión de Marcas</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <FiPlus /> Nueva Marca
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingBrand ? 'Editar' : 'Nueva'} Marca</h2>
              <button className="close-btn" onClick={resetForm}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="form">
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Logo</label>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  accept="image/*"
                />
                {uploading && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ width: '100%', backgroundColor: '#eee', borderRadius: '4px', height: '10px' }}>
                      <div
                        style={{
                          width: `${uploadProgress}%`,
                          backgroundColor: '#2563eb',
                          height: '100%',
                          borderRadius: '4px',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </div>
                    <p style={{ fontSize: '0.8em', color: '#666', textAlign: 'center', marginTop: '5px' }}>Subiendo: {uploadProgress}%</p>
                  </div>
                )}
                <input
                  type="text"
                  value={formData.logo_path}
                  readOnly
                  placeholder="La ruta se generará automáticamente"
                />
                {formData.logo_path && (
                  <img src={`/api/files/${formData.logo_path}`} alt="Vista previa" className="form-preview" />
                )}
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Activo
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Vista Previa</th>
              <th>Descripción</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {brands.map((brand) => (
              <tr key={brand.id}>
                <td>{brand.id}</td>
                <td>{brand.name}</td>
                <td>
                  {brand.logo_path ? (
                    <img
                      src={`/api/files/${brand.logo_path}`}
                      alt={brand.name}
                      className="table-preview"
                    />
                  ) : '-'}
                </td>
                <td className="description">{brand.description || '-'}</td>
                <td>{brand.is_active ? 'Activo' : 'Inactivo'}</td>
                <td>
                  <button
                    className="btn-icon"
                    onClick={() => handleEdit(brand)}
                    title="Editar"
                  >
                    <FiEdit />
                  </button>
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => handleDelete(brand.id)}
                    title="Eliminar"
                  >
                    <FiTrash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BrandsManagement;
